import { useEffect, useState } from 'react';
import 'swiper/swiper.min.css';

import { Swiper, SwiperSlide } from 'swiper/swiper-react';
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper/modules';
import SwiperNav from './SwiperNav';
import RekaiFallback from './RekaiFallback';
import { ServicesTitles } from '~js/React/Enums';
import { ForSaleStrings } from '~js/React/types/ForSaleString';
import useMediaQuery from '../CustomHooks/useMediaQuery';
import { capitalizeFirstLetter } from '~js/React/helpers';

const RekaiContent = ({ options = {} }) => {
  const [predictions, setPredictions] = useState([]);
  const isMediumScreen = useMediaQuery('(min-width: 46rem)');
  const isBigScreen = useMediaQuery('(min-width: 64rem)');

  const [swiper, setSwiper] = useState(null);

  interface Prediction {
    url: string;
    title: string;
    pageid: string;
    percent: number;
    type: string;
    ldjson: any;
    description2: string;
    enddate: string;
    h1: string;
    h2: string;
    imgalt: string;
    imgurl: string;
    ingress: string;
    jpgimg: string;
    jpgimgalt: string;
    lang: string;
    locationtext: string;
    metadata: string;
    pubdate: string;
    text: string;
  }

  function doOnePrediction(subtree: string, greenEstate: boolean) {
    greenEstate = greenEstate || false;
    subtree = subtree || '';
    if (subtree === 'popular') {
      subtree = '';
    }

    // Add overwrites
    var options = {
      customer: (window as any).__rekai.customer,
      overwrite: {
        addcontent: true,
        nrofhits: 20,
        subtree: subtree,
        ...(greenEstate && { 'ldjsonfilter.greenEstate': greenEstate }),
      },
    };
    // When we are on test server we add this
    options.customer.projectid = 10141023;
    // Do prediction and then run function renderHouses
    (window as any).__rekai.predict(options, renderResidences);
  }
  function renderResidences(data: any) {
    const _predictions = data.predictions;
    setPredictions(_predictions);
  }
  function btnClick(e: any, filter: string, greenEstate: boolean) {
    doOnePrediction(filter, greenEstate);

    const predictionButtons = document.querySelectorAll('.predictionButton');
    predictionButtons.forEach((btn) => btn.classList.remove('active'));
    e.classList.add('active');
    swiper.slideTo(0, 0);
  }

  useEffect(() => {
    var options = {
      customer: (window as any).__rekai.customer,
      overwrite: {
        addcontent: true,
        nrofhits: 20,
      },
    };
    // Check if the window object and the __rekai object exist
    if (typeof window !== 'undefined' && (window as any).__rekai) {
      // Call the predict method on the __rekai object
      (window as any).__rekai.predict(
        options,
        (data: { predictions: Prediction[] }) => {
          // Get the predictions from the data
          const _predictions = data.predictions;

          // Update the state with the new predictions
          setPredictions(_predictions);
        }
      );
    }
  }, []);

  if (predictions.length === 0) {
    return <RekaiFallback />;
  }
  return (
    <div className="relative mx-auto max-w-[1200px]">
      <div className="mdplus:mx-0 rekaiFilterButtons">
        <div className="relative mt-4">
          <div className="flex mb-3 mdplus:py-1 overflow-x-scroll lg:overflow-x-hidden overflow-y-visible predictionButtonContainer no-scrollbar">
            <button
              onClick={(e) => {
                btnClick(e.target, 'popular', false);
              }}
              className="flex-shrink-0 mr-1 ml-0 font-icb text-black500 hover:text-blue700 text-base mdplus:text-lg duration-200 predictionButton popular active"
            >
              Populära bostäder
            </button>
            <button
              onClick={(e) => {
                btnClick(e.target, 'fritidshus', false);
              }}
              className="flex-shrink-0 mr-1 ml-3 mdplus:ml-5 font-icb text-black500 hover:text-blue700 text-base mdplus:text-lg duration-200 predictionButton fritids"
            >
              Njut av din fritid
            </button>
            <button
              onClick={(e) => {
                btnClick(e.target, 'villa', false);
              }}
              className="flex-shrink-0 mr-1 ml-3 mdplus:ml-5 font-icb text-black500 hover:text-blue700 text-base mdplus:text-lg duration-200 predictionButton villa"
            >
              Drömvillan
            </button>
            <button
              onClick={(e) => {
                btnClick(e.target, 'bostadsratt', false);
              }}
              className="flex-shrink-0 mr-1 ml-3 mdplus:ml-5 font-icb text-black500 hover:text-blue700 text-base mdplus:text-lg duration-200 predictionButton bostadsratt"
            >
              Stadskänsla
            </button>
            <button
              onClick={(e) => {
                btnClick(e.target, '', true);
              }}
              className="flex-shrink-0 ml-3 mdplus:ml-5 pr-18 font-icb text-black500 hover:text-blue700 text-base mdplus:text-lg duration-200 predictionButton hallbart"
            >
              {ServicesTitles.GreenEstate}
            </button>
          </div>
          <div className="md:hidden top-0 right-0 z-10 absolute bg-light-gray-horizontal-gradient w-20 h-full pointer-events-none"></div>
          <button
            aria-label="Klicka för fler bostäder"
            type="button"
            className="right-0 absolute slick-next"
          >
            <img
              alt="Ikon pil åt höger"
              src="/Images/Icons/Arrows/arrow-right-blue700.svg"
            />
          </button>
        </div>
      </div>
      <Swiper
        onSwiper={setSwiper}
        modules={[Navigation, Pagination, Scrollbar, A11y]}
        spaceBetween={16}
        slidesPerView={isMediumScreen ? (isBigScreen ? 3.5 : 2.5) : 1.2}
        className="gap-8 grid grid-cols-3 !-ml-2 pb-8 !pl-2 thumbShow"
        navigation={{
          nextEl: '.review-swiper-button-next',
          prevEl: '.review-swiper-button-previous',
        }}
      >
        <div className="max-w-[1200px] overflow-hidden">
          {predictions.map((prediction, index) => {
            const jsonLdData = prediction?.ldjson?.find(
              (ld: any) => ld['@type'] === 'Product'
            );

            if (!jsonLdData || !jsonLdData?.image) return null;
            return (
              <SwiperSlide className="items-start pb-8 !h-auto" key={index}>
                <a
                  key={prediction?.title}
                  href={prediction?.url}
                  className="flex self-stretch h-full"
                >
                  <div className="rounded-md outline-none w-full h-full objectLinkWrapper">
                    <div className="hover:shadow-bs80 !min-w-0 h-full transition duration-150 objectCard">
                      <img
                        src={jsonLdData?.image}
                        alt={jsonLdData?.name}
                        className="w-full h-48 object-cover objectImage"
                        loading="lazy"
                      />
                      {jsonLdData?.bidding === 'true' ? (
                        <div className="top-4 -left-2 absolute flex flex-col items-end text-base">
                          <div className="inline bg-blue700 mb-1 cornerInfo">
                            <div className="z-10 relative flex items-center bg-blue700 shadow-bs40 px-3 py-1 viewing-content">
                              <div className="border-t-blue950 cornerInfoTriangleLeft"></div>
                              <div className="relative flex mr-2 w-2 h-2">
                                <span className="inline-flex absolute bg-white lg:bg-white bg-opacity-75 rounded-full w-full h-full animate-ping"></span>
                                <span className="inline-flex relative bg-white lg:bg-white rounded-full w-2 h-2"></span>
                              </div>
                              <span className="flex items-center font-icb text-white whitespace-pre">
                                {ForSaleStrings.Bidding}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <></>
                      )}

                      <div className="flex flex-col justify-center px-3 md:px-4 py-4 min-h-[6.5rem]">
                        <p className="flex items-center !m-0 text-black500">
                          {capitalizeFirstLetter(
                            jsonLdData?.location.toLowerCase()
                          )}
                        </p>
                        <h3 className="mt-1 mb-2 font-icb text-black700 text-lf text-left">
                          {jsonLdData?.name}
                        </h3>
                        <p className="flex flex-wrap gap-x-4 gap-y-1 my-0 mb-2 max-w-xs2 font-icb text-tiny">
                          {jsonLdData?.offers?.price &&
                            jsonLdData?.offers?.price !== 'Pris ej angivet' &&
                            jsonLdData?.offers?.priceCurrency && (
                              <span className="text-black500 infoItem">
                                {jsonLdData.offers.price}{' '}
                                {jsonLdData.offers.priceCurrency.replace(
                                  'SEK',
                                  'kr'
                                )}
                              </span>
                            )}
                          {jsonLdData?.description2 && (
                            <span className="text-black500 infoItem">
                              {jsonLdData.description2}
                            </span>
                          )}
                        </p>
                        <div className="flex flex-wrap">
                          {jsonLdData?.headstart === 'headstart' && (
                            <div className="flex flex-wrap">
                              <span className="mr-2 text-black500 text-tiny">
                                {ServicesTitles.HeadStart}
                              </span>
                            </div>
                          )}
                          {jsonLdData?.ldplus === 'ldplus' && (
                            <div className="flex flex-wrap">
                              <span className="mr-2 text-black500 text-tiny">
                                {ServicesTitles.LD}
                              </span>
                            </div>
                          )}
                          {jsonLdData?.greenEstate === 'true' && (
                            <div
                              className="flex flex-wrap"
                              title="Bra energiklass innebär att bostaden har energideklaration klass A eller B"
                            >
                              <span className="mr-2 text-black500 text-tiny">
                                {ServicesTitles.GreenEstate}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              </SwiperSlide>
            );
          })}
        </div>
      </Swiper>
      <SwiperNav isPrev={true} />
      <SwiperNav isPrev={false} />
    </div>
  );
};

export default RekaiContent;
